import { useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { useParams } from "react-router-dom";

import { DatasetEditorContext } from "..";

import { ENDPOINTS } from "@/api/application-api";
import { useChannel } from "@/components/Messaging/useChannel";
import { WEBSOCKET_MESSAGE_NAME } from "@/constants";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import { displayErrorToast, displayToast } from "@/utils/toast";

export const useDeleteRowsProgress = () => {
  const { user } = useUser();
  const context = useContext(DatasetEditorContext);
  const queryClient = useQueryClient();
  const { datasetId } = useParams();

  useChannel(
    `user:${user?.id}`,
    WEBSOCKET_MESSAGE_NAME.DATASET_FILE_PROCESSING_PROGRESS,
    socketMessage => {
      const data = JSON.parse(socketMessage.data);

      if (data.payload.dataset_id !== Number(datasetId)) {
        return;
      }

      if (
        data.payload?.type === "dataset_rows_deletion" &&
        context?.processingType === "deleteRows"
      ) {
        context?.setProcessingProgress?.(data.payload.progress);
      }
    },
  );

  useChannel(
    `user:${user?.id}`,
    WEBSOCKET_MESSAGE_NAME.DATASET_FILE_PROCESSING_COMPLETE,
    async socketMessage => {
      const data = JSON.parse(socketMessage.data);

      if (data.dataset_id !== Number(datasetId)) {
        return;
      }

      if (data.type === "dataset_rows_deletion" && context?.processingType === "deleteRows") {
        context?.setProcessingType?.(null);
        context?.setProcessingProgress?.(0);

        if (data.success) {
          await queryClient.invalidateQueries({
            queryKey: [ENDPOINTS.dataset_groups],
          });
          await queryClient.invalidateQueries({
            queryKey: [ENDPOINTS.datasets],
          });
          await queryClient.invalidateQueries({
            queryKey: [ENDPOINTS.dataset_rows],
          });

          displayToast(data.message || "All rows deleted successfully", ToastType.success);
        } else {
          displayErrorToast(data.message || "Failed to delete rows");
        }
      }
    },
  );
};
